<template>
  <section class="gm-panel gm-panel--flush compare-matrix">
    <div class="compare-matrix__scroll gm-scroll">
      <table class="matrix">
        <thead>
          <tr>
            <th class="matrix__corner">
              <span class="matrix__corner-label">Measured at grade {{ grade }}</span>
              <span class="matrix__corner-note">roster median in this column</span>
            </th>
            <th
              v-for="build in builds"
              :key="build.archetype.id"
              class="matrix__head"
              :class="`path-${pathSlug(build.archetype)}`"
            >
              <button
                type="button"
                class="matrix__open"
                :title="`Open ${build.archetype.name}`"
                @click="$emit('open', build.archetype.id)"
              >
                <img
                  v-if="build.archetype.icon"
                  class="matrix__art"
                  :src="build.archetype.icon"
                  :alt="build.archetype.name"
                />
                <span v-else class="matrix__art matrix__art--empty">{{ initials(build.archetype) }}</span>
                <span class="matrix__name">{{ build.archetype.name }}</span>
                <MetatypeBadge
                  size="sm"
                  :metatype="build.profile.verdict.metatype"
                  :runner-up="build.profile.verdict.runner_up"
                  :hybrid="build.profile.verdict.is_hybrid"
                />
              </button>
              <button
                type="button"
                class="matrix__close"
                :aria-label="`Close ${build.archetype.name}`"
                @click="$emit('close', build.archetype.id)"
              >&times;</button>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr class="matrix__row matrix__row--text">
            <th scope="row">Path · rank</th>
            <td v-for="build in builds" :key="build.archetype.id">
              {{ shortPath(build.archetype) }} · {{ build.profile.rank_name }}
            </td>
          </tr>

          <tr
            v-for="row in rows"
            :key="row.label"
            class="matrix__row"
            :class="{ 'is-group-start': row.groupStart }"
          >
            <th scope="row">
              <span class="matrix__label">{{ row.label }}</span>
              <span v-if="row.median !== null" class="matrix__median">{{ row.median }}</span>
            </th>
            <td
              v-for="(value, index) in row.values"
              :key="index"
              class="matrix__cell"
              :class="{
                'is-best': row.better && value !== null && value === row.best,
                'is-worst': row.better && value !== null && value === row.worst && row.best !== row.worst,
              }"
            >
              {{ format(value) }}
            </td>
          </tr>

          <tr class="matrix__row matrix__row--mix is-group-start">
            <th scope="row"><span class="matrix__label">What it does with a turn</span></th>
            <td v-for="build in builds" :key="build.archetype.id">
              <span class="mix">
                <span
                  v-for="entry in mix(build.profile)"
                  :key="entry.name"
                  class="mix__seg"
                  :class="`fill-${entry.name.toLowerCase()}`"
                  :style="{ width: `${entry.weight * 100}%` }"
                  :title="`${entry.name} ${Math.round(entry.weight * 100)}%`"
                ></span>
              </span>
              <span class="mix__legend">
                <span v-for="entry in mix(build.profile).slice(0, 3)" :key="entry.name">
                  {{ entry.name.slice(0, 3) }} {{ Math.round(entry.weight * 100) }}%
                </span>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import MetatypeBadge from './MetatypeBadge.vue';

const props = defineProps({
  builds: { type: Array, required: true },
  band: { type: Object, default: null },
  grade: { type: Number, required: true },
});

defineEmits(['open', 'close']);

// Every row is one measurement read straight across the selection, which is the
// question the four side-by-side scrolling columns made hard: they put the same
// number at a different height in every column.
const DEFINITIONS = [
  { label: 'HP', pick: (p) => p.resources.hp, better: 'high', median: (b) => b?.median_hp, groupStart: true },
  { label: 'Energy', pick: (p) => p.resources.energy, better: 'high' },
  { label: 'Action points', pick: (p) => p.resources.ap, better: 'high' },
  { label: 'Stat budget', pick: (p) => p.total_stats, better: null },
  {
    label: 'Damage / turn',
    pick: (p) => p.rollup.single_target_dpt,
    better: 'high',
    median: (b) => b?.median_single_target_dpt,
    groupStart: true,
  },
  // Against the field, not against itself. `turns_to_fall` divides a build's
  // own health by its own damage, so tripling the damage and halving the health
  // leaves it unchanged — it cannot show imbalance between two builds, which is
  // the only thing this table is for. Kept below as the mirror-match reading.
  {
    label: 'Turns to fall (vs field)',
    pick: (p) => p.rollup.turns_to_fall_vs_field,
    better: 'high',
    median: (b) => b?.median_turns_to_fall,
  },
  {
    label: 'Turns to fall (mirror)',
    pick: (p) => p.rollup.turns_to_fall,
    better: 'high',
  },
  { label: 'Burst', pick: (p) => p.rollup.burst_power, better: 'high' },
  // Reach, targets and the two costs of holding a turn. They belong beside the
  // damage rather than buried in the skill table: with movement priced at an
  // action point a cell, a build that reaches three cells is paying for its
  // damage in a currency the damage row cannot show.
  { label: 'Reach (cells)', pick: (p) => p.rollup.engagement_reach, better: 'high' },
  { label: 'Targets hit', pick: (p) => p.rollup.effective_targets, better: 'high' },
  { label: 'Sustain (turns)', pick: (p) => p.rollup.sustain_turns, better: 'high' },
  { label: 'Health / turn', pick: (p) => p.rollup.health_per_turn, better: 'low' },
  { label: 'Healing / turn', pick: (p) => p.rollup.healing_per_turn, better: null },
  { label: 'Mitigation / turn', pick: (p) => p.rollup.mitigation_per_turn, better: null },
  { label: 'Best power / AP', pick: (p) => p.rollup.best_power_per_ap, better: 'high' },
  { label: 'Skills castable', pick: (p) => p.skills.length, better: 'high', groupStart: true },
  {
    label: 'Schools open',
    pick: (p) => p.schools.filter((school) => school.is_open).length,
    better: 'high',
  },
  { label: 'Skills off curve', pick: (p) => p.rollup.off_curve_count, better: 'low' },
];

const rows = computed(() =>
  DEFINITIONS.map((definition) => {
    const values = props.builds.map((build) => normalise(definition.pick(build.profile)));
    const present = values.filter((value) => value !== null);
    const medianRaw = definition.median ? definition.median(props.band) : null;
    const best = present.length
      ? definition.better === 'low'
        ? Math.min(...present)
        : Math.max(...present)
      : null;
    const worst = present.length
      ? definition.better === 'low'
        ? Math.max(...present)
        : Math.min(...present)
      : null;
    return {
      label: definition.label,
      groupStart: definition.groupStart ?? false,
      // A row where every build lands on the same number has no leader, and
      // marking all four green says the opposite.
      better: best === worst ? null : definition.better,
      values,
      median: medianRaw === null || medianRaw === undefined ? null : round(medianRaw),
      best,
      worst,
    };
  }),
);

function normalise(value) {
  if (value === null || value === undefined) return null;
  return round(value);
}

function round(value) {
  return Math.round(value * 10) / 10;
}

function format(value) {
  return value === null ? '—' : value;
}

function mix(profile) {
  return Object.entries(profile.verdict.weights)
    .filter(([, weight]) => weight > 0.001)
    .sort((a, b) => b[1] - a[1])
    .map(([name, weight]) => ({ name, weight }));
}

function initials(archetype) {
  return archetype.name
    .split(/[\s-]+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('');
}

function shortPath(archetype) {
  return (archetype.path ?? '').replace('Path of ', '') || '—';
}

function pathSlug(archetype) {
  return shortPath(archetype).toLowerCase();
}
</script>

<style scoped>
.compare-matrix {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.compare-matrix__scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.matrix {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.matrix__corner,
.matrix__head {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--gm-surface-1);
  border-bottom: 1px solid var(--gm-border);
}

.matrix__corner {
  display: table-cell;
  width: 13rem;
  padding: var(--gm-gap-sm) var(--gm-gap);
  text-align: left;
  vertical-align: bottom;
  z-index: 3;
  left: 0;
}

.matrix__corner-label {
  display: block;
  font-family: var(--gm-font-display);
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--gm-text-muted);
}

.matrix__corner-note {
  display: block;
  font-size: 0.56rem;
  font-style: italic;
  color: var(--gm-text-dim);
}

.matrix__head {
  position: relative;
  padding: var(--gm-gap-sm);
  text-align: left;
  vertical-align: bottom;
  border-top: 2px solid var(--path-color, transparent);
}

.matrix__head.path-json { --path-color: var(--gm-info); }
.matrix__head.path-john { --path-color: var(--gm-accent); }

.matrix__open {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  width: 100%;
  padding: 0 1.2rem 0 0;
  font: inherit;
  text-align: left;
  color: inherit;
  background: none;
  border: none;
  cursor: pointer;
}

.matrix__art {
  flex: 0 0 auto;
  width: 2.4rem;
  height: 2.4rem;
  object-fit: cover;
  object-position: 50% 12%;
  border-radius: var(--gm-radius-sm);
  border: 1px solid var(--gm-border-dim);
  background: var(--gm-surface-sunken);
}

.matrix__art--empty {
  display: grid;
  place-items: center;
  font-family: var(--gm-font-display);
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--gm-text-dim);
}

.matrix__name {
  font-family: var(--gm-font-display);
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.2;
  color: var(--gm-text);
}

.matrix__open:hover .matrix__name {
  color: var(--gm-accent);
}

.matrix__close {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  width: 1.05rem;
  height: 1.05rem;
  display: grid;
  place-items: center;
  font: inherit;
  font-size: 0.85rem;
  line-height: 1;
  color: var(--gm-text-dim);
  background: transparent;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
}

.matrix__close:hover {
  color: var(--gm-text);
  border-color: var(--gm-border);
}

.matrix__row th {
  position: sticky;
  left: 0;
  z-index: 1;
  padding: 0.28rem var(--gm-gap);
  text-align: left;
  font-weight: 400;
  background: var(--gm-surface-0);
  border-top: 1px solid var(--gm-border-dim);
}

.matrix__row td {
  padding: 0.28rem var(--gm-gap-sm);
  border-top: 1px solid var(--gm-border-dim);
  color: var(--gm-text);
  font-variant-numeric: tabular-nums;
}

.matrix__row:hover th,
.matrix__row:hover td {
  background: var(--gm-accent-softer);
}

.matrix__row.is-group-start th,
.matrix__row.is-group-start td {
  border-top: 1px solid var(--gm-border);
}

.matrix__label {
  font-size: 0.7rem;
  color: var(--gm-text-muted);
}

.matrix__median {
  margin-left: 0.4rem;
  font-size: 0.6rem;
  color: var(--gm-text-dim);
  font-variant-numeric: tabular-nums;
}

/* Best and worst are marked on the number itself rather than by shading the
   cell: at thirteen rows a wash of background colour reads as a heat map of
   nothing in particular. */
.matrix__cell.is-best {
  color: var(--gm-success);
  font-weight: 700;
}

.matrix__cell.is-worst {
  color: var(--gm-danger);
}

.matrix__row--text td {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--gm-text-dim);
}

.matrix__row--mix td {
  padding-top: var(--gm-gap-sm);
  padding-bottom: var(--gm-gap-sm);
}

.mix {
  display: flex;
  width: 100%;
  height: 0.45rem;
  border-radius: 999px;
  overflow: hidden;
  background: var(--gm-surface-sunken);
}

.mix__seg {
  display: block;
  height: 100%;
}

.mix__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.2rem;
  font-size: 0.55rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--gm-text-dim);
}

.fill-attacker { background: var(--gm-danger); }
.fill-defender { background: var(--gm-info); }
.fill-healer { background: var(--gm-success); }
.fill-buffer { background: var(--gm-accent-hi); }
.fill-debuffer { background: #a888c9; }
</style>
